import type { ErrorLike } from '@apollo/client';

interface GqlErr {
  message?: string;
  extensions?: { code?: unknown; [k: string]: unknown };
}

/**
 * One readable line out of an Apollo 4 mutation rejection.
 *
 * Apollo 4 hands a rejection back in two different shapes and the scheduler hits
 * both: a 200 with an `errors` array arrives as CombinedGraphQLErrors (`.errors`),
 * while our api answers an auth refusal with a non-200, so HttpLink raises a
 * ServerError whose `message` is only "Response not successful: Received status
 * code 403" and whose real payload sits on `result`. Reading `message` alone
 * turns "scheduler writes require root|engineer" into a status code — the same
 * trap `isEgressQueryUnavailable` documents on the query side.
 *
 * The GraphQL `extensions.code` is appended because it is the part an operator
 * can act on (FORBIDDEN → ask for a role; NOT_FOUND → wrong job name).
 */
export function mutationErrorMessage(error: ErrorLike | undefined | null): string | null {
  if (!error) return null;
  const e = error as { message?: string; errors?: GqlErr[]; result?: unknown; bodyText?: string };

  // Every shape below is guarded rather than trusted: these fields come off the
  // wire (a proxy's HTML 502 page, a gateway's `{"error":"..."}`), so `errors`
  // being an array is a hope, not a contract — spreading a non-array throws, and
  // the throw would happen inside a render.
  const gqlErrors: GqlErr[] = Array.isArray(e.errors) ? [...e.errors] : [];
  // ServerError: the GraphQL errors are in the response body, not on the error.
  // Apollo 4 exposes it as `result` when it parsed the JSON and as `bodyText`
  // when it did not — a browser run against a 403 stub showed bodyText is the one
  // that actually arrives for our api, so both are read.
  const result = e.result as { errors?: unknown } | undefined;
  if (Array.isArray(result?.errors)) gqlErrors.push(...(result.errors as GqlErr[]));
  if (!gqlErrors.length && typeof e.bodyText === 'string' && e.bodyText.trim()) {
    try {
      const parsed = JSON.parse(e.bodyText) as { errors?: unknown } | null;
      if (Array.isArray(parsed?.errors)) gqlErrors.push(...(parsed.errors as GqlErr[]));
    } catch {
      // A body that is not JSON (an nginx error page, a plain string) tells us
      // nothing; `message` is still rendered below.
    }
  }

  const lines = gqlErrors
    .map((g) => {
      if (!g || typeof g !== 'object') return '';
      const msg = g?.message?.trim();
      if (!msg) return '';
      const code = typeof g?.extensions?.code === 'string' ? g.extensions.code : '';
      return code ? `${msg} (${code})` : msg;
    })
    .filter(Boolean);

  if (lines.length) return lines.join(' · ');
  return e.message?.trim() || 'Request failed.';
}
